import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import type { AcmColumn } from '../types/kpi.js';
import {
  CHART_COLORS,
  TOOLTIP_CONTENT_STYLE,
  AXIS_TICK_STYLE,
  GRID_PROPS,
  LEGEND_CLICKABLE_PROPS,
} from '../lib/chart-theme.js';
import { useSeriesToggle } from '../hooks/useSeriesToggle.js';

export interface ChartDataPoint {
  label: string;
  [key: string]: string | number;
}

interface KpiBlockChartProps {
  title: string;
  columns: AcmColumn[];
  data: ChartDataPoint[];
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business ブロック（会員数/スタイリング/チケット返却）単位の棒グラフ。指標を重ねて時系列の推移を表示する。凡例クリックで系列の表示/非表示を切り替え可能。
 * @graph-connects none
 */
export function KpiBlockChart({ title, columns, data }: KpiBlockChartProps) {
  const { hiddenSeries, handleLegendClick } = useSeriesToggle();

  if (data.length === 0) return null;

  const visibleColumns = columns.slice(0, 5);

  return (
    <Card className="p-4">
      <h3 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">{title}</h3>
      <div style={{ width: '100%', height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
            <YAxis
              tick={AXIS_TICK_STYLE}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => v.toLocaleString('ja-JP')}
            />
            <Tooltip
              contentStyle={TOOLTIP_CONTENT_STYLE}
              formatter={
                ((value: number, name: string) => [value.toLocaleString('ja-JP'), name]) as never
              }
            />
            <Legend {...LEGEND_CLICKABLE_PROPS} onClick={handleLegendClick} />
            {visibleColumns.map((col, i) => (
              <Bar
                key={col.key}
                dataKey={col.key}
                name={col.label}
                fill={CHART_COLORS[i % CHART_COLORS.length]}
                hide={hiddenSeries.has(col.key)}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      {columns.length > 5 && <OverflowMetrics columns={columns.slice(5)} data={data} />}
    </Card>
  );
}

interface OverflowMetricsProps {
  columns: AcmColumn[];
  data: ChartDataPoint[];
}

/** @graph-connects none */
function OverflowMetrics({ columns, data }: OverflowMetricsProps) {
  const latest = data[data.length - 1];

  return (
    <div className="mt-3 flex flex-wrap gap-3 border-t border-[var(--color-border)] pt-3">
      {columns.map((col) => (
        <div key={col.key} className="text-xs text-[var(--color-text-secondary)]">
          <span className="font-medium">{col.label}</span>:{' '}
          <span className="tabular-nums text-[var(--color-text-primary)]">
            {(latest[col.key] as number).toLocaleString('ja-JP')}
          </span>
        </div>
      ))}
    </div>
  );
}
